import React, { useState } from 'react'
import { Form, Input, Checkbox } from 'semantic-ui-react'
import { AppPrimaryButton, AppSecondaryButton } from 'elements'
import { ActionPlanRuleService } from 'utility'
import { InlineVendorCard, VendorManageActions } from './styles'
import {
    getApiErrorMessage,
    normalizeVendorCompanyFromResponse,
    normalizeVendorContactFromResponse,
} from './utils'

const handleVendorApiError = (err, setFieldError, onServerError) => {
    const status = err?.status
    if (status === 400) {
        setFieldError(getApiErrorMessage(err) || 'Please check the required fields.')
        return
    }
    if (status === 404) {
        onServerError?.('This vendor isn\'t available anymore.')
        return
    }
    if (status === 500) {
        onServerError?.('Something went wrong. Please try again.')
        return
    }
    onServerError?.(getApiErrorMessage(err))
}

export const AddVendorCompanyForm = ({ onCancel, onCreated, onServerError }) => {
    const [vendorName, setVendorName] = useState('')
    const [error, setError] = useState('')
    const [saving, setSaving] = useState(false)

    const handleCreate = async () => {
        const trimmed = vendorName.trim()
        if (!trimmed) {
            setError('Vendor name is required.')
            return
        }
        setError('')
        setSaving(true)
        try {
            const res = await ActionPlanRuleService.createVendorCompany({
                VendorName: trimmed,
            })
            onCreated?.(normalizeVendorCompanyFromResponse(res))
        } catch (err) {
            handleVendorApiError(err, setError, onServerError)
        } finally {
            setSaving(false)
        }
    }

    return (
        <InlineVendorCard>
            <div className="card-title">+ Add vendor company</div>
            <Form>
                <Form.Field required error={Boolean(error)}>
                    <label>Vendor name</label>
                    <Input
                        placeholder="e.g. Acme Cleaning Co."
                        value={vendorName}
                        onChange={(_, { value }) => {
                            setVendorName(value)
                            if (error) setError('')
                        }}
                        onKeyDown={e => {
                            if (e.key === 'Enter') {
                                e.preventDefault()
                                handleCreate()
                            }
                        }}
                    />
                    {error && <div className="field-error">{error}</div>}
                </Form.Field>
            </Form>
            <div className="card-actions">
                <AppSecondaryButton type="button" onClick={onCancel} disabled={saving}>
                    Cancel
                </AppSecondaryButton>
                <AppPrimaryButton type="button" onClick={handleCreate} loading={saving}>
                    Create
                </AppPrimaryButton>
            </div>
        </InlineVendorCard>
    )
}

export const AddVendorContactForm = ({
    vendorCompanyId,
    onCancel,
    onCreated,
    onServerError,
}) => {
    const [fullName, setFullName] = useState('')
    const [email, setEmail] = useState('')
    const [phone, setPhone] = useState('')
    const [whatsAppNumber, setWhatsAppNumber] = useState('')
    const [isPrimary, setIsPrimary] = useState(false)
    const [error, setError] = useState('')
    const [saving, setSaving] = useState(false)

    const handleCreate = async () => {
        const trimmedName = fullName.trim()
        if (!trimmedName) {
            setError('Full name is required.')
            return
        }
        if (!vendorCompanyId) {
            setError('Select a vendor company first.')
            return
        }
        setError('')
        setSaving(true)
        try {
            const res = await ActionPlanRuleService.createVendorContact({
                VendorCompanyId: Number(vendorCompanyId),
                FullName: trimmedName,
                Email: email.trim(),
                Phone: phone.trim(),
                WhatsAppNumber: whatsAppNumber.trim(),
                IsPrimary: isPrimary,
            })
            onCreated?.(normalizeVendorContactFromResponse(res))
        } catch (err) {
            handleVendorApiError(err, setError, onServerError)
        } finally {
            setSaving(false)
        }
    }

    return (
        <InlineVendorCard>
            <div className="card-title">+ Add vendor contact</div>
            <Form>
                <Form.Field required error={Boolean(error && !fullName.trim())}>
                    <label>Full name</label>
                    <Input
                        placeholder="Jane Operations"
                        value={fullName}
                        onChange={(_, { value }) => {
                            setFullName(value)
                            if (error) setError('')
                        }}
                    />
                </Form.Field>
                <div className="form-row-2">
                    <Form.Field>
                        <label>Email</label>
                        <Input
                            placeholder="jane@acme.example.com"
                            value={email}
                            onChange={(_, { value }) => setEmail(value)}
                        />
                    </Form.Field>
                    <Form.Field>
                        <label>Phone</label>
                        <Input
                            placeholder="+1 555 0100"
                            value={phone}
                            onChange={(_, { value }) => setPhone(value)}
                        />
                    </Form.Field>
                </div>
                <div className="form-row-2">
                    <Form.Field>
                        <label>WhatsApp number</label>
                        <Input
                            placeholder="+1 555 0100"
                            value={whatsAppNumber}
                            onChange={(_, { value }) => setWhatsAppNumber(value)}
                        />
                    </Form.Field>
                    <Form.Field>
                        <label>Primary contact</label>
                        <div className="checkbox-row">
                            <Checkbox
                                checked={isPrimary}
                                onChange={(_, { checked }) => setIsPrimary(checked)}
                            />
                            <span>Mark as primary</span>
                        </div>
                    </Form.Field>
                </div>
                {error && <div className="field-error">{error}</div>}
            </Form>
            <div className="card-actions">
                <AppSecondaryButton type="button" onClick={onCancel} disabled={saving}>
                    Cancel
                </AppSecondaryButton>
                <AppPrimaryButton type="button" onClick={handleCreate} loading={saving}>
                    Create
                </AppPrimaryButton>
            </div>
        </InlineVendorCard>
    )
}

export const EditVendorCompanyForm = ({
    vendor,
    onCancel,
    onUpdated,
    onServerError,
}) => {
    const [vendorName, setVendorName] = useState(vendor?.name || '')
    const [error, setError] = useState('')
    const [saving, setSaving] = useState(false)

    const handleSave = async () => {
        const trimmed = vendorName.trim()
        if (!trimmed) {
            setError('Vendor name is required.')
            return
        }
        if (!vendor?.id) return
        setError('')
        setSaving(true)
        try {
            const res = await ActionPlanRuleService.updateVendorCompany(
                vendor.id,
                { VendorName: trimmed },
            )
            onUpdated?.(normalizeVendorCompanyFromResponse(res))
        } catch (err) {
            handleVendorApiError(err, setError, onServerError)
        } finally {
            setSaving(false)
        }
    }

    return (
        <InlineVendorCard>
            <div className="card-title">Edit vendor company</div>
            <Form>
                <Form.Field required error={Boolean(error)}>
                    <label>Vendor name</label>
                    <Input
                        placeholder="e.g. Acme Cleaning Co."
                        value={vendorName}
                        onChange={(_, { value }) => {
                            setVendorName(value)
                            if (error) setError('')
                        }}
                        onKeyDown={e => {
                            if (e.key === 'Enter') {
                                e.preventDefault()
                                handleSave()
                            }
                        }}
                    />
                    {error && <div className="field-error">{error}</div>}
                </Form.Field>
            </Form>
            <div className="card-actions">
                <AppSecondaryButton type="button" onClick={onCancel} disabled={saving}>
                    Cancel
                </AppSecondaryButton>
                <AppPrimaryButton type="button" onClick={handleSave} loading={saving}>
                    Save
                </AppPrimaryButton>
            </div>
        </InlineVendorCard>
    )
}

export const EditVendorContactForm = ({
    contact,
    onCancel,
    onUpdated,
    onServerError,
}) => {
    const [fullName, setFullName] = useState(contact?.name || '')
    const [email, setEmail] = useState(contact?.email || '')
    const [phone, setPhone] = useState(contact?.phone || '')
    const [whatsAppNumber, setWhatsAppNumber] = useState(
        contact?.whatsAppNumber || '',
    )
    const [isPrimary, setIsPrimary] = useState(Boolean(contact?.isPrimary))
    const [error, setError] = useState('')
    const [saving, setSaving] = useState(false)

    const handleSave = async () => {
        const trimmedName = fullName.trim()
        if (!trimmedName) {
            setError('Full name is required.')
            return
        }
        if (!contact?.id) return
        setError('')
        setSaving(true)
        try {
            const res = await ActionPlanRuleService.updateVendorContact(
                contact.id,
                {
                    FullName: trimmedName,
                    Email: email.trim(),
                    Phone: phone.trim(),
                    WhatsAppNumber: whatsAppNumber.trim(),
                    IsPrimary: isPrimary,
                },
            )
            onUpdated?.(normalizeVendorContactFromResponse(res))
        } catch (err) {
            handleVendorApiError(err, setError, onServerError)
        } finally {
            setSaving(false)
        }
    }

    return (
        <InlineVendorCard>
            <div className="card-title">Edit vendor contact</div>
            <Form>
                <Form.Field required error={Boolean(error && !fullName.trim())}>
                    <label>Full name</label>
                    <Input
                        placeholder="Jane Operations"
                        value={fullName}
                        onChange={(_, { value }) => {
                            setFullName(value)
                            if (error) setError('')
                        }}
                    />
                </Form.Field>
                <div className="form-row-2">
                    <Form.Field>
                        <label>Email</label>
                        <Input
                            placeholder="jane@acme.example.com"
                            value={email}
                            onChange={(_, { value }) => setEmail(value)}
                        />
                    </Form.Field>
                    <Form.Field>
                        <label>Phone</label>
                        <Input
                            placeholder="+1 555 0100"
                            value={phone}
                            onChange={(_, { value }) => setPhone(value)}
                        />
                    </Form.Field>
                </div>
                <div className="form-row-2">
                    <Form.Field>
                        <label>WhatsApp number</label>
                        <Input
                            placeholder="+1 555 0100"
                            value={whatsAppNumber}
                            onChange={(_, { value }) => setWhatsAppNumber(value)}
                        />
                    </Form.Field>
                    <Form.Field>
                        <label>Primary contact</label>
                        <div className="checkbox-row">
                            <Checkbox
                                checked={isPrimary}
                                onChange={(_, { checked }) => setIsPrimary(checked)}
                            />
                            <span>Mark as primary</span>
                        </div>
                    </Form.Field>
                </div>
                {error && <div className="field-error">{error}</div>}
            </Form>
            <div className="card-actions">
                <AppSecondaryButton type="button" onClick={onCancel} disabled={saving}>
                    Cancel
                </AppSecondaryButton>
                <AppPrimaryButton type="button" onClick={handleSave} loading={saving}>
                    Save
                </AppPrimaryButton>
            </div>
        </InlineVendorCard>
    )
}

export const VendorManageBar = ({
    label,
    onEdit,
    onDelete,
    deleting,
    confirmDelete,
    deleteWarning,
}) => (
    <VendorManageActions>
        {label && <span className="manage-label">{label}</span>}
        <AppSecondaryButton type="button" size="small" onClick={onEdit} disabled={deleting}>
            Edit
        </AppSecondaryButton>
        <AppSecondaryButton
            type="button"
            size="small"
            onClick={onDelete}
            disabled={deleting}
            style={{ color: '#b91c1c' }}
        >
            {confirmDelete ? 'Confirm delete?' : 'Delete'}
        </AppSecondaryButton>
        {confirmDelete && deleteWarning && (
            <div className="delete-warning">{deleteWarning}</div>
        )}
    </VendorManageActions>
)
